import React, { useEffect, useMemo, useState } from 'react'
import {
	Box,
	Collapse,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import type { DriverSituationModalRow, DriverSituationsModalData } from '../../../../../types/modal'

type DensityMode = 'compact' | 'standard'

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const parseDateValue = (value?: string): Dayjs | null => {
	if (!value) {
		return null
	}

	const [day, month, year] = value.split('/')
	if (!day || !month || !year) {
		return null
	}

	const parsed = dayjs(`${year}-${month}-${day}`)
	return parsed.isValid() ? parsed : null
}

const formatDateValue = (value: Dayjs | null): string => (value ? value.format('DD/MM/YYYY') : '')

// Invisible but clickable opener so picker still opens when clicking input area.
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
	},
}

const sendButtonSx = {
	height: '32px',
	minHeight: '32px',
	minWidth: '59px',
	padding: '6px 12px',
	borderRadius: '4px',
	fontSize: '14px',
	fontWeight: 600,
	lineHeight: '24px',
	fontFamily: 'Poppins, sans-serif',
	textTransform: 'none',
	boxShadow: 'none',
	backgroundImage: 'linear-gradient(90.0556deg, #092056 0%, #00A5FF 99.972%)',
	color: '#FFFFFF',
	'&:hover': {
		backgroundImage: 'linear-gradient(90.0556deg, #092056 0%, #00A5FF 99.972%)',
	},
}

const disabledInputSx = {
	...inputSx,
	'& .MuiOutlinedInput-root': {
		...inputSx['& .MuiOutlinedInput-root'],
		backgroundColor: '#F5F7FC',
		'& fieldset': {
			borderColor: 'transparent',
		},
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		lineHeight: 1.3,
		color: '#092056',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
		paddingLeft: '8px',
		paddingRight: '8px',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const defaultRows: DriverSituationModalRow[] = [
	{
		id: '1',
		inicio: 'sex 10/05/2024',
		fim: 'sex 10/05/2024',
		dias: '0',
		idSituacao: '3',
		situacao: 'Folga Atrasada',
		descricao: 'Lorem Ipsum',
		idRegisto: '73252',
		idRegistoOk: '000073252',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
	{
		id: '2',
		inicio: 'sáb 01/06/2024',
		fim: 'sex 01/07/2024',
		dias: '28',
		idSituacao: '9',
		situacao: 'Outra Situação',
		descricao: '—',
		idRegisto: '73182',
		idRegistoOk: '000073182',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
	{
		id: '3',
		inicio: 'sáb 02/07/2024',
		fim: 'sex 03/08/2024',
		dias: '31',
		idSituacao: '9',
		situacao: 'Outra Situação',
		descricao: '—',
		idRegisto: '73182',
		idRegistoOk: '000073182',
		utilizador: 'Rita Cisneiros',
		dataRegisto: 'qui 09/05/2024',
	},
]

const columns: GridColDef<DriverSituationModalRow>[] = [
	{ field: 'inicio', headerName: 'Início', minWidth: 140, flex: 1 },
	{ field: 'fim', headerName: 'Fim', minWidth: 140, flex: 1 },
	{ field: 'dias', headerName: 'Dias', width: 70 },
	{ field: 'idSituacao', headerName: 'ID Situação', minWidth: 110, flex: 0.8 },
	{ field: 'situacao', headerName: 'Situação', minWidth: 130, flex: 1 },
	{ field: 'descricao', headerName: 'Descrição', minWidth: 155, flex: 1.2 },
	{ field: 'idRegisto', headerName: 'ID Registo', minWidth: 110, flex: 0.8 },
	{ field: 'idRegistoOk', headerName: 'ID Registo OK', minWidth: 125, flex: 0.9 },
	{ field: 'utilizador', headerName: 'Utilizador', minWidth: 125, flex: 1 },
	{ field: 'dataRegisto', headerName: 'Data Registo', minWidth: 130, flex: 1 },
]

const CustomModalDriverSituations: React.FC = () => {
	const { isOpen, closeModal, modalData } = useModal()
	const data = modalData as DriverSituationsModalData | undefined

	const [density, setDensity] = useState<DensityMode>('compact')
	const [createSituationOpen, setCreateSituationOpen] = useState(false)
	const [selectedRows, setSelectedRows] = useState<Array<string | number>>([])
	const [rows, setRows] = useState<DriverSituationModalRow[]>(data?.rows?.length ? data.rows : defaultRows)
	const [startDate, setStartDate] = useState<Dayjs | null>(parseDateValue(data?.startDate ?? '01/05/2024'))
	const [endDate, setEndDate] = useState<Dayjs | null>(parseDateValue(data?.endDate ?? '01/09/2024'))
	const [situationFilter, setSituationFilter] = useState(data?.situacao ?? 'Todas')
	const [newSituation, setNewSituation] = useState({
		inicio: '15/08/2024',
		fim: '15/08/2024',
		situacao: '09 - Outras',
		descricao: 'Lorem Ipsum',
	})

	useEffect(() => {
		setRows(data?.rows?.length ? data.rows : defaultRows)
		setStartDate(parseDateValue(data?.startDate ?? '01/05/2024'))
		setEndDate(parseDateValue(data?.endDate ?? '01/09/2024'))
		setSituationFilter(data?.situacao ?? 'Todas')
		setSelectedRows([])
		setCreateSituationOpen(false)
	}, [data])

	const totalDays = useMemo(
		() =>
			rows.reduce((sum, row) => {
				const parsed = Number(row.dias)
				return Number.isFinite(parsed) ? sum + parsed : sum
			}, 0),
		[rows],
	)

	const driverName = data?.driverName ?? 'Z-Aldina'
	const folgaAtual = data?.folgaAtual ?? 'Segunda-Feira'
	const complementarAtual = data?.complementarAtual ?? 'Terça-Feira'

	const handleAddSituation = () => {
		const nextRegistryId = String(70000 + rows.length + 1)

		setRows(prev => [
			...prev,
			{
				id: `${Date.now()}`,
				inicio: newSituation.inicio,
				fim: newSituation.fim,
				dias: '0',
				idSituacao: '9',
				situacao: newSituation.situacao,
				descricao: newSituation.descricao || '—',
				idRegisto: nextRegistryId,
				idRegistoOk: nextRegistryId.padStart(9, '0'),
				utilizador: driverName,
				dataRegisto: newSituation.inicio,
			},
		])
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Box sx={{ display: 'flex', flexDirection: 'column' }}>
					<Box sx={{ padding: '16px 24px 0' }}>
						<Typography
							sx={{
								fontFamily: 'Poppins, sans-serif',
								fontWeight: 600,
								fontSize: '20px',
								lineHeight: 1.3,
								color: '#092056',
							}}
						>
							Situações do Motorista - {driverName}
						</Typography>
					</Box>

					<Box sx={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
								gap: '16px',
								justifyContent: 'space-between',
							}}
						>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<DatePicker
									label='Data Inicial'
									value={startDate}
									onChange={value => setStartDate(value)}
									format='DD/MM/YYYY'
									slotProps={{
										textField: {
											size: 'small',
											sx: [inputSx, calendarInputSx, { minWidth: '140px' }],
										},
										...calendarPopperSlotProps,
									}}
									slots={{ openPickerButton: EmptyButton }}
								/>
								<Typography
									sx={{
										fontFamily: 'Poppins, sans-serif',
										fontSize: '13px',
										fontWeight: 500,
										lineHeight: 1.3,
										color: '#092056',
										alignSelf: 'center',
									}}
								>
									—
								</Typography>
								<DatePicker
									label='Data Final'
									value={endDate}
									onChange={value => setEndDate(value)}
									format='DD/MM/YYYY'
									slotProps={{
										textField: {
											size: 'small',
											sx: [inputSx, calendarInputSx, { minWidth: '140px' }],
										},
										...calendarPopperSlotProps,
									}}
									slots={{ openPickerButton: EmptyButton }}
								/>
								<TextField
									select
									label='Situação'
									size='small'
									value={situationFilter}
									onChange={event => setSituationFilter(event.target.value)}
									SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
									sx={{ ...inputSx, minWidth: '220px' }}
								>
									<MenuItem value='Todas'>Todas</MenuItem>
									<MenuItem value='Folga Atrasada'>Folga Atrasada</MenuItem>
									<MenuItem value='Outra Situação'>Outra Situação</MenuItem>
								</TextField>
								<TextField
									size='small'
									label='Folga Atual'
									value={folgaAtual}
									sx={{ ...disabledInputSx, minWidth: '220px' }}
									disabled
								/>
								<TextField
									size='small'
									label='Compl. Atual'
									value={complementarAtual}
									sx={{ ...disabledInputSx, minWidth: '220px' }}
									disabled
								/>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									textTransform: 'none',
									padding: '4px 0',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								flexWrap: 'wrap',
								gap: '16px',
							}}
						>
							<Typography
								sx={{
									fontFamily: 'Poppins, sans-serif',
									fontWeight: 600,
									fontSize: '24px',
									lineHeight: 1.334,
									color: '#092056',
								}}
							>
								{rows.length} Situações
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
								<TextField
									size='small'
									label='Total Dias'
									value={String(totalDays)}
									sx={{ ...disabledInputSx, minWidth: '93px' }}
									disabled
								/>
								<CustomButton
									variant='text'
									size='small'
									sx={{
										color: '#00A5FF',
										textTransform: 'none',
										padding: '4px 0',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '22px',
									}}
									endIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
									onClick={() => setCreateSituationOpen(prev => !prev)}
								>
									Criar Situação
								</CustomButton>
								<CustomButton
									size='small'
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '24px',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
									}}
								>
									Folgas Atrasadas
								</CustomButton>
								<CustomButton
									size='small'
									sx={{
										background: '#00A5FF',
										color: '#FFFFFF',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '24px',
										textTransform: 'none',
										borderRadius: '4px',
										padding: '6px 12px',
									}}
								>
									Calendário Folgas
								</CustomButton>
								<ToggleButtonGroup
									value={density}
									exclusive
									onChange={(_, nextDensity) => nextDensity && setDensity(nextDensity)}
									size='small'
									sx={toggleGroupSx}
								>
									<ToggleButton
										value='compact'
										aria-label='densidade compacta'
									>
										<TableRowsIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
									<ToggleButton
										value='standard'
										aria-label='densidade espaçada'
									>
										<ViewHeadlineIcon
											sx={{
												width: '14.4px',
												height: '14.4px',
												color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
											}}
										/>
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Collapse
							in={createSituationOpen}
							timeout={250}
							unmountOnExit
						>
							<Box
								sx={{
									backgroundColor: 'rgba(245, 247, 252, 0.5)',
									borderRadius: '5px',
									padding: '16px',
									display: 'flex',
									flexDirection: 'column',
									gap: '16px',
								}}
							>
								<Typography
									sx={{
										fontFamily: 'Poppins, sans-serif',
										fontWeight: 600,
										fontSize: '20px',
										lineHeight: 1.3,
										color: '#092056',
									}}
								>
									Criar Nova Situação
								</Typography>
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
									<DatePicker
										label='Data Inicial'
										value={parseDateValue(newSituation.inicio)}
										onChange={value =>
											setNewSituation(prev => ({
												...prev,
												inicio: formatDateValue(value),
											}))
										}
										format='DD/MM/YYYY'
										slotProps={{
											textField: {
												size: 'small',
												sx: [inputSx, calendarInputSx, { minWidth: '160px' }],
											},
											...calendarPopperSlotProps,
										}}
										slots={{ openPickerButton: EmptyButton }}
									/>
									<DatePicker
										label='Data Final'
										value={parseDateValue(newSituation.fim)}
										onChange={value =>
											setNewSituation(prev => ({
												...prev,
												fim: formatDateValue(value),
											}))
										}
										format='DD/MM/YYYY'
										slotProps={{
											textField: {
												size: 'small',
												sx: [inputSx, calendarInputSx, { minWidth: '160px' }],
											},
											...calendarPopperSlotProps,
										}}
										slots={{ openPickerButton: EmptyButton }}
									/>
									<TextField
										select
										label='Situação'
										size='small'
										value={newSituation.situacao}
										onChange={event => setNewSituation(prev => ({ ...prev, situacao: event.target.value }))}
										SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
										sx={{ ...inputSx, minWidth: '220px', flex: '1 1 220px' }}
									>
										<MenuItem value='09 - Outras'>09 - Outras</MenuItem>
										<MenuItem value='Folga Atrasada'>Folga Atrasada</MenuItem>
									</TextField>
									<TextField
										label='Observações'
										size='small'
										value={newSituation.descricao}
										onChange={event => setNewSituation(prev => ({ ...prev, descricao: event.target.value }))}
										sx={{ ...inputSx, minWidth: '260px', flex: '1 1 320px' }}
									/>
								</Box>
								<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
									<CustomButton
										variant='text'
										size='small'
										onClick={() => setCreateSituationOpen(false)}
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											fontSize: '14px',
											textTransform: 'none',
											padding: '4px 0',
										}}
									>
										Cancelar
									</CustomButton>
									<CustomButton
										variant='text'
										size='small'
										onClick={handleAddSituation}
										sx={{
											color: '#00A5FF',
											fontWeight: 600,
											fontSize: '14px',
											textTransform: 'none',
											padding: '4px 0',
										}}
									>
										Adicionar
									</CustomButton>
								</Box>
							</Box>
						</Collapse>

						<Box
							sx={{
								border: '1px solid rgba(134, 149, 175, 0.15)',
								borderRadius: '6px',
								overflow: 'hidden',
							}}
						>
							<AnimatedDataGridPro
								slots={{ columnMenu: CustomColumnMenu }}
								slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
								rows={rows}
								columns={columns}
								density={density}
								rowHeight={density === 'compact' ? 32 : 40}
								columnHeaderHeight={32}
								autoHeight
								checkboxSelection
								disableRowSelectionOnClick
								hideFooter
								rowSelectionModel={selectedRows}
								onRowSelectionModelChange={selectionModel => setSelectedRows([...selectionModel])}
								sx={[tableSx, dataGridDensityTransitionSx]}
							/>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							gap: '8px',
							padding: '8px',
						}}
					>
						<CustomButton
							variant='text'
							size='small'
							onClick={closeModal}
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '24px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Fechar
						</CustomButton>
						<CustomButton
							variant='text'
							size='small'
							sx={{
								color: '#00A5FF',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '24px',
								textTransform: 'none',
								padding: '6px 8px',
							}}
						>
							Imprimir
						</CustomButton>
						<CustomButton
							size='small'
							sx={sendButtonSx}
						>
							Enviar
						</CustomButton>
					</Box>
				</Box>
			</LocalizationProvider>
		</Dialog>
	)
}

export default CustomModalDriverSituations
